import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Paper
} from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import ShieldIcon from '@mui/icons-material/Shield';
import { api } from '../../api';

interface ModalChaveSegurancaProps {
  open: boolean;
  onClose: () => void;
  clienteID: string;
}

export const ModalChaveSeguranca: React.FC<ModalChaveSegurancaProps> = ({
  open,
  onClose,
  clienteID
}) => {
  const [etapa, setEtapa] = useState<'inicial' | 'validando' | 'revelada'>('inicial');
  const [carregando, setCarregando] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [emailMascarado, setEmailMascarado] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);

  // Estados para modo de suporte / administrador global
  const [modoSuporte, setModoSuporte] = useState(false);
  const [emailSuporte, setEmailSuporte] = useState('fsatecnologias@gmail.com');
  const [motivoSuporte, setMotivoSuporte] = useState('');

  const [dadosChave, setDadosChave] = useState<{
    tipoChave: 'privada' | 'padrao';
    chave: string;
    tituloChave: string;
    descricao: string;
    instrucoes: string;
  } | null>(null);

  const reiniciarEstado = () => {
    setEtapa('inicial');
    setCarregando(false);
    setCodigo('');
    setEmailMascarado('');
    setErro(null);
    setCopiado(false);
    setDadosChave(null);
    setModoSuporte(false);
    setMotivoSuporte('');
  };

  const lidarComFechamento = () => {
    reiniciarEstado();
    onClose();
  };

  const solicitarCodigo = async () => {
    setCarregando(true);
    setErro(null);
    try {
      if (modoSuporte) {
        if (!motivoSuporte || motivoSuporte.trim().length < 5) {
          setErro('Por favor, informe o motivo ou número do chamado de suporte (mínimo 5 caracteres).');
          setCarregando(false);
          return;
        }
        const response = await api.post('/v2/admin/chave-seguranca/solicitar-codigo', {
          clienteID,
          motivo: motivoSuporte.trim(),
          emailAdmin: emailSuporte.trim()
        });
        setEmailMascarado(response.data.emailMascarado || emailSuporte);
      } else {
        const response = await api.post('/v2/cliente/chave-seguranca/solicitar-codigo', {
          clienteID
        });
        setEmailMascarado(response.data.emailMascarado || 'e-mail cadastrado');
      }
      setEtapa('validando');
    } catch (err: any) {
      console.error(err);
      const msg = err.response?.data?.mensagem || 'Não foi possível enviar o código de verificação. Contate o suporte.';
      setErro(msg);
    } finally {
      setCarregando(false);
    }
  };

  const validarCodigo = async () => {
    if (!codigo || codigo.trim().length !== 6) {
      setErro('Por favor, informe o código de verificação de 6 dígitos.');
      return;
    }

    setCarregando(true);
    setErro(null);
    try {
      let response;
      if (modoSuporte) {
        response = await api.post('/v2/admin/chave-seguranca/revelar', {
          clienteID,
          codigo: codigo.trim(),
          motivo: motivoSuporte.trim(),
          emailAdmin: emailSuporte.trim()
        });
      } else {
        response = await api.post('/v2/cliente/chave-seguranca/revelar', {
          clienteID,
          codigo: codigo.trim()
        });
      }
      setDadosChave(response.data);
      setEtapa('revelada');
    } catch (err: any) {
      console.error(err);
      const msg = err.response?.data?.mensagem || 'Código de verificação incorreto ou expirado.';
      setErro(msg);
    } finally {
      setCarregando(false);
    }
  };

  const copiarChave = () => {
    if (dadosChave?.chave) {
      navigator.clipboard.writeText(dadosChave.chave);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 3000);
    }
  };

  const baixarKitEmergencia = () => {
    if (!dadosChave) return;

    const dataHora = new Date().toLocaleString('pt-BR');
    const conteudoTxt = `======================================================================
BACKUP FÁCIL - KIT DE RECUPERAÇÃO E CHAVE DE SEGURANÇA
======================================================================
Data de Emissão: ${dataHora}
Identificador do Cliente: ${clienteID}
Tipo de Criptografia: ${dadosChave.tituloChave}

CHAVE DE DESCRIPTOGRAFIA:
${dadosChave.chave}

======================================================================
INSTRUÇÕES DE RESTAURAÇÃO:
======================================================================
1. Ao baixar qualquer arquivo de backup pelo Portal do Backup Fácil,
   você receberá um contêiner comprimido no formato (.7z).

2. Para descompactar o arquivo:
   - Abra o utilitário 7-Zip (ou outro descompactador compatível);
   - Selecione a opção "Extrair arquivos...";
   - Quando for solicitada a senha, insira exatamente a chave acima.

3. Restauração do Banco de Dados:
   - Os arquivos de dump do PostgreSQL possuem formato customizado (.backup).
   - Utilize o comando oficial do PostgreSQL para restaurar:
     pg_restore -h localhost -p 5432 -U postgres -d [NOME_BANCO] --clean --no-owner [ARQUIVO.backup]

IMPORTANTE: Guarde este arquivo em um local extremamente seguro!
======================================================================`;

    const blob = new Blob([conteudoTxt], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Kit_Recuperacao_BackupFacil_${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog
      open={open}
      onClose={lidarComFechamento}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <ShieldIcon color="primary" sx={{ fontSize: 28 }} />
        <Typography variant="h6" fontWeight={700}>
          Chave de Segurança do Backup
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2.5 }}>
        {erro && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>
            {erro}
          </Alert>
        )}

        {/* ETAPA 1: SOLICITAÇÃO INICIAL */}
        {etapa === 'inicial' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!modoSuporte ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  Por motivos de segurança e conformidade com a <strong>LGPD</strong>, a chave de descriptografia dos seus arquivos é protegida por autenticação em duas etapas (2FA).
                </Typography>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Como funciona a liberação:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2, m: 0 }}>
                    <li>Enviaremos um código de verificação de 6 dígitos para o e-mail cadastrado da sua empresa.</li>
                    <li>O código é válido por <strong>15 minutos</strong>.</li>
                    <li>Após confirmar o código, você poderá visualizar e baixar sua chave de contingência.</li>
                  </Typography>
                </Paper>

                <Box sx={{ pt: 1, textAlign: 'center' }}>
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => { setModoSuporte(true); setErro(null); }}
                    sx={{ textTransform: 'none', fontSize: '12px' }}
                  >
                    Sou do Suporte Técnico / Não tenho acesso ao e-mail do cliente
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Alert severity="warning" sx={{ mb: 1 }}>
                  <strong>Acesso de Suporte Master:</strong> O código de autorização será enviado para o seu e-mail de Administrador Global, permitindo resgatar a chave sem depender do cliente.
                </Alert>

                <TextField
                  fullWidth
                  size="small"
                  label="E-mail do Administrador / Suporte"
                  value={emailSuporte}
                  onChange={(e) => setEmailSuporte(e.target.value)}
                  placeholder="suporte@backupfacil.com"
                  helperText="O código 2FA de 6 dígitos será enviado para este e-mail corporativo."
                />

                <TextField
                  fullWidth
                  size="small"
                  label="Número do Chamado / Motivo do Atendimento *"
                  value={motivoSuporte}
                  onChange={(e) => setMotivoSuporte(e.target.value)}
                  placeholder="Ex: Chamado #10492 - Cliente parado / restauração de emergência"
                  helperText="Obrigatório para registro em auditoria (LGPD)."
                />

                <Box sx={{ pt: 1, textAlign: 'center' }}>
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => { setModoSuporte(false); setErro(null); }}
                    sx={{ textTransform: 'none', fontSize: '12px' }}
                  >
                    Voltar ao modo padrão do cliente
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}

        {/* ETAPA 2: DIGITAÇÃO DO CÓDIGO */}
        {etapa === 'validando' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 1 }}>
            <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
              Código de verificação enviado para: <strong>{emailMascarado}</strong>
            </Alert>

            <Typography variant="body2" color="text.secondary">
              Digite abaixo o código numérico de 6 dígitos recebido por e-mail:
            </Typography>

            <TextField
              fullWidth
              autoFocus
              label="Código de Verificação (6 dígitos)"
              placeholder="123456"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{
                maxLength: 6,
                style: {
                  fontSize: 24,
                  fontWeight: 700,
                  letterSpacing: 8,
                  textAlign: 'center'
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  validarCodigo();
                }
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button
                size="small"
                onClick={solicitarCodigo}
                disabled={carregando}
                sx={{ textTransform: 'none' }}
              >
                Reenviar código
              </Button>
            </Box>
          </Box>
        )}

        {/* ETAPA 3: CHAVE REVELADA */}
        {etapa === 'revelada' && dadosChave && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {dadosChave.tituloChave}
              </Typography>
              <Chip
                size="small"
                icon={<KeyIcon />}
                label={dadosChave.tipoChave === 'privada' ? 'Zero-Knowledge (Exclusiva)' : 'Chave Padrão do Sistema'}
                color={dadosChave.tipoChave === 'privada' ? 'success' : 'primary'}
                variant="outlined"
              />
            </Box>

            <Typography variant="body2" color="text.secondary">
              {dadosChave.descricao}
            </Typography>

            {/* Box da Chave */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'grey.100',
                border: '1px dashed',
                borderColor: 'grey.400',
                position: 'relative'
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  fontWeight: 600,
                  wordBreak: 'break-all',
                  color: 'primary.dark',
                  userSelect: 'all',
                  pr: 5
                }}
              >
                {dadosChave.chave}
              </Typography>

              <Tooltip title={copiado ? 'Copiado!' : 'Copiar chave'}>
                <IconButton
                  onClick={copiarChave}
                  size="small"
                  color={copiado ? 'success' : 'primary'}
                  sx={{ position: 'absolute', top: 12, right: 12 }}
                >
                  {copiado ? <CheckCircleOutlineIcon /> : <ContentCopyIcon />}
                </IconButton>
              </Tooltip>
            </Paper>

            <Alert severity="success">
              {dadosChave.instrucoes}
            </Alert>

            <Button
              variant="outlined"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={baixarKitEmergencia}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Baixar Kit de Emergência (.txt)
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {etapa === 'inicial' && (
          <>
            <Button onClick={lidarComFechamento} color="inherit" disabled={carregando}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={solicitarCodigo}
              disabled={carregando}
              startIcon={carregando ? <CircularProgress size={18} color="inherit" /> : <ShieldIcon />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {carregando
                ? 'Enviando...'
                : modoSuporte
                ? 'Enviar Código para o Suporte'
                : 'Enviar Código por E-mail'}
            </Button>
          </>
        )}

        {etapa === 'validando' && (
          <>
            <Button onClick={() => setEtapa('inicial')} color="inherit" disabled={carregando}>
              Voltar
            </Button>
            <Button
              variant="contained"
              onClick={validarCodigo}
              disabled={carregando || codigo.length !== 6}
              startIcon={carregando ? <CircularProgress size={18} color="inherit" /> : <KeyIcon />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {carregando ? 'Validando...' : 'Confirmar e Revelar Chave'}
            </Button>
          </>
        )}

        {etapa === 'revelada' && (
          <Button variant="contained" onClick={lidarComFechamento} sx={{ textTransform: 'none' }}>
            Concluído
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ModalChaveSeguranca;
